// ===================================================================
// components/MemoryDuelGame.tsx
// Memory Duel — jeu de memory tour par tour entre deux amis.
// Chaque joueur retourne 2 cartes à son tour ; une paire trouvée fait
// rejouer le même joueur, sinon la main passe à l'adversaire.
// ===================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from './ThemeContext';
import FloatingBackButton from './FloatingBackButton';
import TribalBorder from './TribalBorder';
import './ChallengeMode.css';
import './MemoryDuelGame.css';
import { supabase } from '../services/supabaseAudioService';
import { getFollowing, searchUsers, followUser, UserProfile } from '../services/friendsService';
import {
  createMemoryDuel,
  getMyMemoryDuels,
  getMemoryDuel,
  acceptMemoryDuel,
  declineMemoryDuel,
  revealCard,
  resolveTurn,
  MemoryDuel,
} from '../services/memoryDuelService';
import { LanguageCode } from '../types/types';
import { recordActivity } from '../hooks/useDailyStreak';
import { Illustration, Picto, EmojiText } from './common/Picto';
interface MemoryDuelGameProps {
  languageCode: LanguageCode;
  user: any | null;
  onBackToMenu: () => void;
}

type Screen = 'menu' | 'board';
type Tab = 'create' | 'list';

const RESOLVE_DELAY_MS = 1100;

const MemoryDuelGame: React.FC<MemoryDuelGameProps> = ({ languageCode, user, onBackToMenu }) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  const [screen, setScreen] = useState<Screen>('menu');
  const [tab, setTab] = useState<Tab>('create');

  const [friends, setFriends] = useState<UserProfile[]>([]);
  const [loadFriends, setLoadFriends] = useState(false);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [addResults, setAddResults] = useState<UserProfile[]>([]);
  const [addLoading, setAddLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const [myDuels, setMyDuels] = useState<MemoryDuel[]>([]);
  const [loadingDuels, setLoadingDuels] = useState(false);

  const [activeDuel, setActiveDuel] = useState<MemoryDuel | null>(null);
  const resolvingRef = useRef(false);
  const xpAwardedRef = useRef<string | null>(null);

  // ── Amis ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id || tab !== 'create') return;
    setLoadFriends(true);
    getFollowing(user.id).then(setFriends).catch(() => setFriends([])).finally(() => setLoadFriends(false));
  }, [user, tab]);

  const handleAddSearch = useCallback(async (q: string) => {
    setAddQuery(q);
    if (q.trim().length < 2) { setAddResults([]); return; }
    setAddLoading(true);
    try {
      const results = await searchUsers(q);
      setAddResults(results.filter(r => r.id !== user?.id && !friends.some(f => f.id === r.id)));
    } catch { setAddResults([]); }
    finally { setAddLoading(false); }
  }, [user, friends]);

  const handleFollowFromSearch = useCallback(async (profile: UserProfile) => {
    if (!user?.id) return;
    try {
      await followUser(user.id, profile.id);
      setFriends(prev => [...prev, profile]);
      setAddResults(prev => prev.filter(r => r.id !== profile.id));
    } catch { /* ignore */ }
  }, [user]);

  // ── Mes parties ─────────────────────────────────────────────────
  const refreshDuels = useCallback(() => {
    if (!user?.id) return;
    setLoadingDuels(true);
    getMyMemoryDuels(user.id).then(setMyDuels).catch(() => setMyDuels([])).finally(() => setLoadingDuels(false));
  }, [user]);

  useEffect(() => {
    if (tab === 'list') refreshDuels();
  }, [tab, refreshDuels]);

  // ── Créer une partie ────────────────────────────────────────────
  const handleStartDuel = useCallback(async (friend: UserProfile) => {
    if (!user?.id) return;
    setCreating(true);
    try {
      const id = await createMemoryDuel(user.id, friend.id, languageCode);
      const duel = await getMemoryDuel(id);
      if (duel) { setActiveDuel(duel); setScreen('board'); }
    } catch { /* ignore */ }
    finally { setCreating(false); }
  }, [user, languageCode]);

  const handleOpenDuel = useCallback(async (duel: MemoryDuel) => {
    if (duel.status === 'pending' && duel.player2_id === user?.id) {
      try { await acceptMemoryDuel(duel.id); } catch { /* ignore */ }
    }
    const fresh = await getMemoryDuel(duel.id);
    setActiveDuel(fresh ?? duel);
    setScreen('board');
  }, [user]);

  const handleDeclineDuel = useCallback(async (duel: MemoryDuel) => {
    try { await declineMemoryDuel(duel.id); refreshDuels(); } catch { /* ignore */ }
  }, [refreshDuels]);

  // ── Realtime : suit la partie en cours ─────────────────────────
  useEffect(() => {
    if (!activeDuel?.id) return;
    const channel = supabase
      .channel(`memory-duel-${activeDuel.id}`)
      .on(
        'postgres_changes' as any,
        { event: 'UPDATE', schema: 'public', table: 'memory_duels', filter: `id=eq.${activeDuel.id}` },
        (payload: any) => setActiveDuel(payload.new as MemoryDuel)
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeDuel?.id]);

  const isMyTurn = !!activeDuel && !!user?.id && activeDuel.current_turn === user.id;
  const opponentId = activeDuel ? (activeDuel.player1_id === user?.id ? activeDuel.player2_id : activeDuel.player1_id) : null;
  const opponentName = activeDuel
    ? (activeDuel.player1_id === user?.id ? activeDuel.player2_username : activeDuel.player1_username) || t('challenge.player')
    : '';

  // ── Jouer une carte ─────────────────────────────────────────────
  const handleCardClick = useCallback(async (cardId: string) => {
    if (!activeDuel || !isMyTurn || resolvingRef.current) return;
    if (activeDuel.status !== 'active') return;
    if (activeDuel.revealed.length >= 2) return;
    if (activeDuel.matched.includes(cardId) || activeDuel.revealed.includes(cardId)) return;

    // La face de la carte n'est connue qu'une fois validée par le serveur
    let updated: MemoryDuel;
    try {
      updated = await revealCard(activeDuel.id, cardId);
    } catch {
      return;
    }
    setActiveDuel(prev => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));

    if (updated.revealed.length === 2) {
      resolvingRef.current = true;
      setTimeout(async () => {
        try {
          const resolved = await resolveTurn(updated.id);
          setActiveDuel(prev => (prev && prev.id === resolved.id ? { ...prev, ...resolved } : prev));
        } catch { /* ignore */ }
        resolvingRef.current = false;
      }, RESOLVE_DELAY_MS);
    }
  }, [activeDuel, isMyTurn]);

  // ── Fin de partie : XP + streak (une seule fois par partie) ──────
  useEffect(() => {
    if (!activeDuel || activeDuel.status !== 'completed' || !user?.id) return;
    if (xpAwardedRef.current === activeDuel.id) return;
    xpAwardedRef.current = activeDuel.id;

    const myPairs = activeDuel.scores[user.id] ?? 0;
    const oppPairs = opponentId ? (activeDuel.scores[opponentId] ?? 0) : 0;
    const won = myPairs > oppPairs;
    const xpGained = myPairs * 15 + (won ? 30 : 0);

    try {
      const xpKey = `${languageCode}-totalXP`;
      const newTotal = (parseInt(localStorage.getItem(xpKey) || '0', 10)) + xpGained;
      localStorage.setItem(xpKey, String(newTotal));
      window.dispatchEvent(new CustomEvent('xpUpdated', { detail: { newTotal, gained: xpGained, source: 'memoryDuel' } }));
    } catch { /* ignore */ }

    recordActivity();
  }, [activeDuel, user, opponentId, languageCode]);

  const handleBackFromBoard = useCallback(() => {
    setActiveDuel(null);
    setScreen('menu');
    setTab('list');
  }, []);

  const card_ = 'var(--mg-card)';
  const border = 'var(--mg-border)';
  const gold = 'var(--mg-gold)';

  if (!user?.id) {
    return (
      <div className="chal-login-screen" data-theme={theme}>
        <FloatingBackButton onBack={onBackToMenu} position="top-left" variant="minimal" />
        <div className="chal-login-icon">𓂀</div>
        <p className="chal-login-title">{t('memoryDuel.title', 'Memory Duel')}</p>
        <p className="chal-login-sub">{t('challenge.loginRequired')}</p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════
  // PLATEAU DE JEU
  // ═══════════════════════════════════════════════
  if (screen === 'board' && activeDuel) {
    const myScore = activeDuel.scores[user.id] ?? 0;
    const oppScore = opponentId ? (activeDuel.scores[opponentId] ?? 0) : 0;

    if (activeDuel.status === 'pending') {
      return (
        <div className="chal-screen" data-theme={theme}>
          <TribalBorder />
          <FloatingBackButton onBack={handleBackFromBoard} position="top-left" variant="minimal" />
          <div className="chal-empty" style={{ minHeight: '60vh', justifyContent: 'center' }}>
            <span className="chal-empty__icon">𓇳</span>
            <p className="chal-empty__text">{t('memoryDuel.waitingForOpponent', { name: opponentName, defaultValue: 'En attente que {{name}} accepte le défi…' })}</p>
          </div>
        </div>
      );
    }

    if (activeDuel.status === 'declined') {
      return (
        <div className="chal-screen" data-theme={theme}>
          <TribalBorder />
          <FloatingBackButton onBack={handleBackFromBoard} position="top-left" variant="minimal" />
          <div className="chal-empty" style={{ minHeight: '60vh', justifyContent: 'center' }}>
            <span className="chal-empty__icon">𓂀</span>
            <p className="chal-empty__text">{t('memoryDuel.declined', 'Ce défi a été refusé.')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="memoryduel-board" data-theme={theme}>
        <TribalBorder />
        <div className="memoryduel-header">
          <FloatingBackButton onBack={handleBackFromBoard} mode="inline" variant="minimal" />
          <div className="memoryduel-scoreboard">
            <div className={`memoryduel-score${isMyTurn && activeDuel.status === 'active' ? ' memoryduel-score--active' : ''}`}>
              <span className="memoryduel-score-name">{t('challenge.you', 'Vous')}</span>
              <span className="memoryduel-score-value">{myScore}</span>
            </div>
            <span className="memoryduel-vs">𓆸</span>
            <div className={`memoryduel-score${!isMyTurn && activeDuel.status === 'active' ? ' memoryduel-score--active' : ''}`}>
              <span className="memoryduel-score-name">{opponentName}</span>
              <span className="memoryduel-score-value">{oppScore}</span>
            </div>
          </div>
        </div>

        {activeDuel.status === 'active' && (
          <p className="memoryduel-turn-banner" style={{ color: isMyTurn ? gold : undefined }}>
            {isMyTurn ? t('memoryDuel.yourTurn', 'À vous de jouer !') : t('memoryDuel.opponentTurn', { name: opponentName, defaultValue: 'Tour de {{name}}…' })}
          </p>
        )}

        <div className="memoryduel-grid">
          {activeDuel.cards.map(card => {
            const isFaceUp = activeDuel.matched.includes(card.id) || activeDuel.revealed.includes(card.id);
            const isMatched = activeDuel.matched.includes(card.id);
            // Face publiée par le serveur uniquement pour les cartes visibles
            const face = activeDuel.faces?.[card.id];
            return (
              <button
                key={card.id}
                className={`memoryduel-card${isFaceUp ? ' memoryduel-card--flipped' : ''}${isMatched ? ' memoryduel-card--matched' : ''}`}
                onClick={() => handleCardClick(card.id)}
                disabled={!isMyTurn || isFaceUp || activeDuel.status !== 'active'}
              >
                <div className="memoryduel-card-inner">
                  <div className="memoryduel-card-back">𓋹</div>
                  <div className="memoryduel-card-front">
                    {face?.illustration && <span className="memoryduel-card-icon"><Illustration value={face.illustration} /></span>}
                    <span className="memoryduel-card-text">{face?.text ?? ''}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {activeDuel.status === 'completed' && (
          <div className="memoryduel-results" style={{ background: card_, borderColor: border }}>
            <div style={{ fontSize: '48px' }}><EmojiText text={myScore > oppScore ? '🏆' : myScore === oppScore ? '𓆸' : '𓂀'} /></div>
            <h2>{myScore > oppScore ? t('challenge.victory') : myScore === oppScore ? t('challenge.tie') : t('challenge.defeat')}</h2>
            <p>{myScore} — {oppScore}</p>
            <button className="chal-btn" onClick={handleBackFromBoard}>{t('memoryDuel.backToList', 'Retour à mes parties')}</button>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════
  // MENU (créer / mes parties)
  // ═══════════════════════════════════════════════
  return (
    <div className="chal-screen" data-theme={theme}>
      <TribalBorder />
      <div className="chal-header">
        <FloatingBackButton onBack={onBackToMenu} mode="inline" variant="minimal" />
        <h1 className="chal-header__title">𓋹 {t('memoryDuel.title', 'Memory Duel')}</h1>
        <div className="chal-header__spacer" />
      </div>

      <div className="chal-tabs">
        <button onClick={() => setTab('create')} className={`chal-tab${tab === 'create' ? ' chal-tab--active' : ''}`}>{t('challenge.tabs.create')}</button>
        <button onClick={() => setTab('list')} className={`chal-tab${tab === 'list' ? ' chal-tab--active' : ''}`}>{t('memoryDuel.myGames', 'Mes parties')}</button>
      </div>

      <div className="chal-content">
        {tab === 'create' && (
          <div>
            <div className="chal-section-head">
              <h2 className="chal-section-title">{t('challenge.chooseFriend')}</h2>
              <button
                onClick={() => { setShowAddFriend(v => !v); setAddQuery(''); setAddResults([]); }}
                className={`chal-add-btn${showAddFriend ? ' chal-add-btn--active' : ''}`}
              >
                {showAddFriend ? t('challenge.close') : t('challenge.add')}
              </button>
            </div>

            {showAddFriend && (
              <div className="chal-search-panel">
                <p className="chal-search-label">{t('challenge.searchUser')}</p>
                <input type="text" value={addQuery} onChange={e => handleAddSearch(e.target.value)} placeholder={t('challenge.usernamePlaceholder') as string} className="chal-search-input" />
                {addLoading && <p className="chal-search-hint">{t('challenge.loading')}</p>}
                {addResults.map(r => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                    <div className="chal-avatar" style={{ width: '36px', height: '36px', fontSize: '15px' }}>{(r.display_name || r.username || '?')[0].toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <p className="chal-friend-name" style={{ fontSize: '14px' }}>{r.display_name || r.username || t('challenge.player')}</p>
                    </div>
                    <button onClick={() => handleFollowFromSearch(r)} className="chal-follow-btn">{t('challenge.follow')}</button>
                  </div>
                ))}
              </div>
            )}

            {loadFriends && <p className="chal-search-hint">{t('challenge.loading')}</p>}
            {!loadFriends && friends.length === 0 && !showAddFriend && (
              <div className="chal-empty">
                <span className="chal-empty__icon">𓀀</span>
                <p className="chal-empty__text">{t('challenge.noFriendsYet')}</p>
              </div>
            )}
            {friends.map(f => (
              <button key={f.id} onClick={() => handleStartDuel(f)} disabled={creating} className="chal-friend-row">
                <div className="chal-avatar">{(f.display_name || f.username || '?')[0].toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <p className="chal-friend-name">{f.display_name || f.username || t('challenge.player')}</p>
                  <p className="chal-friend-sub"><Picto name="cartesMemoire" /> {t('memoryDuel.startGame', 'Lancer un Memory')}</p>
                </div>
                <span className="chal-friend-arrow">›</span>
              </button>
            ))}
          </div>
        )}

        {tab === 'list' && (
          <div>
            <h2 className="chal-section-title" style={{ marginBottom: '14px' }}>{t('memoryDuel.myGames', 'Mes parties')}</h2>
            {loadingDuels && <p className="chal-search-hint">{t('challenge.loading')}</p>}
            {!loadingDuels && myDuels.length === 0 && (
              <div className="chal-empty">
                <span className="chal-empty__icon">𓏤</span>
                <p className="chal-empty__text">{t('memoryDuel.noGames', 'Aucune partie pour le moment.')}</p>
              </div>
            )}
            {myDuels.map(d => {
              const iAmP1 = d.player1_id === user.id;
              const oppName = (iAmP1 ? d.player2_username : d.player1_username) || t('challenge.player');
              const isInvite = d.status === 'pending' && !iAmP1;
              const isWaiting = d.status === 'pending' && iAmP1;
              const mine = d.scores[user.id] ?? 0;
              const theirId = iAmP1 ? d.player2_id : d.player1_id;
              const theirs = d.scores[theirId] ?? 0;

              return (
                <div key={d.id} className="chal-recv-card">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="chal-avatar" style={{ width: '40px', height: '40px', fontSize: '16px' }}>{oppName[0]?.toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <p className="chal-friend-name">vs <span style={{ color: gold }}>{oppName}</span></p>
                      <p className="chal-friend-sub">
                        <Picto name="cartesMemoire" /> Memory
                        {d.status === 'completed' && ` · ${mine} — ${theirs}`}
                        {isWaiting && ` · ${t('memoryDuel.pendingSent', 'en attente de réponse')}`}
                      </p>
                    </div>
                  </div>
                  {isInvite && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => handleOpenDuel(d)} className="chal-btn">{t('challenge.takeChallenge')}</button>
                      <button onClick={() => handleDeclineDuel(d)} className="chal-btn" style={{ opacity: 0.6 }}>✕</button>
                    </div>
                  )}
                  {!isInvite && !isWaiting && d.status === 'active' && (
                    <button onClick={() => handleOpenDuel(d)} className="chal-btn">{t('memoryDuel.resume', 'Reprendre')}</button>
                  )}
                  {d.status === 'completed' && (
                    <button onClick={() => handleOpenDuel(d)} className="chal-btn" style={{ opacity: 0.7 }}>{t('memoryDuel.review', 'Revoir')}</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemoryDuelGame;
